<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick, computed } from 'vue'
import VideoActions from './video-actions.vue'
import VideoFeedback from './video-feedback.vue'
import type { FeedPreset, IRefVideoItem } from '..'
import { useUserStore } from '@/stores/user'
import { useHistoryStore } from '@/stores/history'
import { useAppStore } from '@/stores/app'
import Comments from './comments'
import Episodes from './episodes'
import FooterBarForDesktop from './footerbar/pc.vue'
import FooterForMobile from './footerbar/mobile.vue'
const appStore = useAppStore()
const userStore = useUserStore()
const historyStore = useHistoryStore()
const isLogin = computed(() => userStore.isLogin)

const props = defineProps<{
  idx: number
  item: IRefVideoItem
  items: IRefVideoItem[]
  isActive: boolean // 是否是当前活跃视频
  playbackEnabled?: boolean
  hiddenActions?: boolean
  preset?: FeedPreset
  showComments?: boolean
  showEpisodes?: boolean
}>()

// 是否为付费视频
const isPremium = computed(() => !props.item.free && !props.item.url)

const emit = defineEmits<{
  (e: 'like'): void
  (e: 'collect'): void
  (e: 'share'): void
  (e: 'comment'): void
  (e: 'episodes'): void
  (e: 'clear'): void
  (e: 'select-episode', episodeId: number): void
  (e: 'primary-action', sourceId: number): void
}>()

// 内部状态管理
const videoRef = ref<HTMLVideoElement | null>(null)
const isError = ref(false)
const isPaused = ref(true)
const isBuffering = ref(false)
const progress = ref(0)
const userControlled = ref(false) // 用户是否手动控制过
const duration = ref(0)
const currentTime = ref(0)
const isMuted = ref(false)
const isNativeFullscreen = ref(false)
const playerRef = ref<HTMLElement | null>(null)
const isProgressDragging = ref(false)
const draggingProgress = ref(0)
const progressDragTarget = ref<HTMLElement | null>(null)

const displayProgress = computed(() => {
  if (isProgressDragging.value) {
    return draggingProgress.value
  }
  return progress.value
})

const isPlaybackActive = computed(() => props.isActive && props.playbackEnabled !== false)
const isCreationPreset = computed(() => props.preset === 'creation')
const shouldHideActions = computed(() => props.hiddenActions || isCreationPreset.value)
const enableComments = computed(() => !isCreationPreset.value && props.showComments !== false)
const enableEpisodes = computed(() => !isCreationPreset.value && props.showEpisodes !== false)
const historyEnabled = computed(() => props.item.historyEnabled !== false && !isCreationPreset.value)

const canControl = computed(() => {
  return !isError.value && !!videoUrl.value
})

// 检查 URL 有效性
const isValidUrl = () => {
  return props.item.url && props.item.url.trim() !== ''
}

// Video 事件处理
const onLoaded = () => {
  isBuffering.value = false
  if (isPlaybackActive.value && !userControlled.value && videoRef.value) {
    videoRef.value.play().catch(() => {})
  }
}

const onWaiting = () => {
  isBuffering.value = true
}

const onCanPlay = () => {
  isBuffering.value = false
  tryRecordInitialHistory()
  // canplay 比 loadeddata 更晚触发，适合作为自动播放的兜底保障
  if (isPlaybackActive.value && !userControlled.value && videoRef.value) {
    videoRef.value.play().catch(() => {})
  }
}

const onPlaying = () => {
  isPaused.value = false
  isBuffering.value = false
}

const onPause = () => {
  isPaused.value = true
}

const onPlay = () => {
  isPaused.value = false
}

const onError = () => {
  console.warn(`视频 ${props.idx} 加载失败:`, props.item.url)
  isError.value = true
  isBuffering.value = false
}

const onTimeUpdate = (e: Event) => {
  const video = e.target as HTMLVideoElement
  currentTime.value = video.currentTime || 0
  duration.value = video.duration || 0
  if (video.duration) {
    progress.value = (video.currentTime / video.duration) * 100
  }
}

const onLoadedMetadata = (e: Event) => {
  const video = e.target as HTMLVideoElement
  duration.value = video.duration || 0
  tryRecordInitialHistory()
}

const onDurationChange = (e: Event) => {
  const video = e.target as HTMLVideoElement
  duration.value = video.duration || 0
}

const onVolumeChange = (e: Event) => {
  const video = e.target as HTMLVideoElement
  isMuted.value = video.muted
}

// 记录播放历史
// 规则：
// 1. 无效视频（空地址）不记录
// 2. 首次播放记录 0%
// 3. 小于 1 分钟的视频仅记录首次 0%
// 4. 大于等于 1 分钟的视频，在离开（切换视频/路由离开）时记录当前进度
const hasRecordedInitial = ref(false)
const hasRecordedLeave = ref(false)
const lastSubmittedWatchDuration = ref<number | null>(null)

const hasValidVideoUrl = computed(() => !!(props.item.url && props.item.url.trim()))
const isLongVideo = computed(() => duration.value >= 60)
const canRecordInitial = computed(() => {
  const video = videoRef.value
  if (!video) return false
  return (
    isPlaybackActive.value && !isError.value && hasValidVideoUrl.value && (video.readyState >= 3 || duration.value > 0)
  )
})

const resetHistoryRecordState = () => {
  hasRecordedInitial.value = false
  hasRecordedLeave.value = false
  lastSubmittedWatchDuration.value = null
}

const submitHistory = (watchDuration: number) => {
  if (!historyEnabled.value || !isLogin.value || !hasValidVideoUrl.value) return

  const normalizedDuration = Math.max(0, Math.floor(watchDuration))
  if (lastSubmittedWatchDuration.value === normalizedDuration) return

  lastSubmittedWatchDuration.value = normalizedDuration
  historyStore.recordHistory({
    EpisodeId: props.item.episodeId,
    ScriptId: props.item.scriptId,
    WatchDuration: normalizedDuration,
  })
}

const recordInitialHistory = () => {
  if (hasRecordedInitial.value) return
  submitHistory(0)
  hasRecordedInitial.value = true
}

const tryRecordInitialHistory = () => {
  if (!canRecordInitial.value) return
  recordInitialHistory()
}

const recordLeaveHistory = () => {
  if (hasRecordedLeave.value) return
  if (!isLongVideo.value) return

  submitHistory(videoRef.value?.currentTime || 0)
  hasRecordedLeave.value = true
}

watch(
  () => [props.item.episodeId, props.item.scriptId, props.item.url],
  () => {
    resetHistoryRecordState()
  },
)

watch(
  () => props.isActive,
  (active, prevActive) => {
    if (active) {
      hasRecordedLeave.value = false
      tryRecordInitialHistory()
      return
    }

    if (prevActive) {
      recordLeaveHistory()
    }
  },
  { immediate: true },
)

watch(
  isPlaybackActive,
  async active => {
    await nextTick()

    if (!videoRef.value || isError.value || !isValidUrl()) return

    if (active) {
      userControlled.value = false
      if (videoRef.value.readyState >= 2) {
        videoRef.value.play().catch(() => {})
      }
      return
    }

    if (!videoRef.value.paused) {
      videoRef.value.pause()
    }
  },
  { immediate: true },
)

// 切换播放/暂停
const togglePlay = () => {
  if (!videoRef.value || isError.value || !isValidUrl()) return

  userControlled.value = true

  if (videoRef.value.paused) {
    videoRef.value.play().catch(() => {})
  } else {
    videoRef.value.pause()
  }
}

const seekToProgress = (nextProgress: number) => {
  if (!videoRef.value || !videoRef.value.duration || isError.value) return
  const safeProgress = Math.max(0, Math.min(100, nextProgress))
  const nextTime = videoRef.value.duration * (safeProgress / 100)
  videoRef.value.currentTime = nextTime
  currentTime.value = nextTime
  progress.value = safeProgress
}

const getProgressByClientX = (clientX: number, target: HTMLElement | null) => {
  if (!target) return 0
  const rect = target.getBoundingClientRect()
  if (!rect.width) return 0
  const ratio = (clientX - rect.left) / rect.width
  return Math.max(0, Math.min(100, ratio * 100))
}

const handlePcProgressClick = (e: MouseEvent) => {
  if (!canControl.value) return
  const target = e.currentTarget as HTMLElement | null
  seekToProgress(getProgressByClientX(e.clientX, target))
}

const clearPcProgressDragListeners = () => {
  window.removeEventListener('mousemove', handlePcProgressMove)
  window.removeEventListener('mouseup', handlePcProgressEnd)
}

const handlePcProgressStart = (e: MouseEvent) => {
  if (!canControl.value) return
  const target = e.currentTarget as HTMLElement | null
  if (!target) return

  progressDragTarget.value = target
  isProgressDragging.value = true
  draggingProgress.value = getProgressByClientX(e.clientX, target)

  window.addEventListener('mousemove', handlePcProgressMove)
  window.addEventListener('mouseup', handlePcProgressEnd)
}

const handlePcProgressMove = (e: MouseEvent) => {
  if (!canControl.value || !isProgressDragging.value || !progressDragTarget.value) return
  draggingProgress.value = getProgressByClientX(e.clientX, progressDragTarget.value)
}

const handlePcProgressEnd = (e: MouseEvent) => {
  if (!isProgressDragging.value || !progressDragTarget.value) {
    clearPcProgressDragListeners()
    return
  }

  draggingProgress.value = getProgressByClientX(e.clientX, progressDragTarget.value)
  isProgressDragging.value = false
  seekToProgress(draggingProgress.value)
  progressDragTarget.value = null
  clearPcProgressDragListeners()
}

const handleProgressTouchStart = (e: TouchEvent) => {
  if (!canControl.value || !e.touches.length) return
  const target = e.currentTarget as HTMLElement | null
  isProgressDragging.value = true
  draggingProgress.value = getProgressByClientX(e.touches[0]!.clientX, target)
}

const handleProgressTouchMove = (e: TouchEvent) => {
  if (!canControl.value || !isProgressDragging.value || !e.touches.length) return
  const target = e.currentTarget as HTMLElement | null
  draggingProgress.value = getProgressByClientX(e.touches[0]!.clientX, target)
}

const handleProgressTouchEnd = () => {
  if (!canControl.value || !isProgressDragging.value) return
  isProgressDragging.value = false
  seekToProgress(draggingProgress.value)
}

const toggleMute = () => {
  if (!videoRef.value) return
  videoRef.value.muted = !videoRef.value.muted
  isMuted.value = videoRef.value.muted
}

const onFullscreenChange = () => {
  isNativeFullscreen.value = document.fullscreenElement === playerRef.value
}

const toggleFullscreen = async () => {
  if (!playerRef.value || !document.fullscreenEnabled) return

  try {
    if (document.fullscreenElement === playerRef.value) {
      await document.exitFullscreen()
      return
    }
    await playerRef.value.requestFullscreen()
  } catch (error) {
    console.warn('全屏切换失败:', error)
  }
}

const handleEpisodesTap = () => {
  if (!enableEpisodes.value) return
  emit('episodes')
  appStore.toggleShowEpisode(props.item.scriptId)
}

const handlePrimaryAction = () => {
  const sourceId = props.item.sourceCreationId ?? props.item.episodeId
  emit('primary-action', sourceId)
}

// 初始化：检查 URL 有效性
onMounted(() => {
  if (!isValidUrl()) {
    isError.value = true
  }
  document.addEventListener('fullscreenchange', onFullscreenChange)
})

onUnmounted(() => {
  recordLeaveHistory()
  document.removeEventListener('fullscreenchange', onFullscreenChange)
  clearPcProgressDragListeners()
})

const videoUrl = computed(() => {
  if (props.item.requiresLogin && !isLogin.value) return ''
  if (isPremium.value) return ''
  return props.item.url || ''
})

const showComment = computed(() => enableComments.value && appStore.showCommentSets.has(props.item.scriptId))
const showEpisodes = computed(() => enableEpisodes.value && appStore.showEpisodeSets.has(props.item.scriptId))
const showCommentPanel = computed({
  get: () => showComment.value,
  set: (value: boolean) => {
    if (!enableComments.value) return
    if (value) {
      appStore.showComments(props.item.scriptId)
    } else if (showComment.value) {
      appStore.hideComments(props.item.scriptId)
    }
  },
})
const showEpisodesPanel = computed({
  get: () => showEpisodes.value,
  set: (value: boolean) => {
    if (!enableEpisodes.value) return
    if (value) {
      appStore.showEpisodes(props.item.scriptId)
    } else if (showEpisodes.value) {
      appStore.hideEpisodes(props.item.scriptId)
    }
  },
})
</script>

<template>
  <div
    class="absolute inset-0 h-full flex overflow-hidden"
    :class="appStore.isMobile ? 'flex-col' : 'flex-row'"
    :style="{ transform: `translateY(${idx * 100}%)` }"
  >
    <div
      v-if="!appStore.isMobile"
      class="absolute inset-0 bg-cover bg-center bg-no-repeat blur-2xl scale-110 pointer-events-none"
      :style="{ 'background-image': `url(${props.item.thumbnail})` }"
      aria-hidden="true"
    ></div>
    <div class="absolute inset-0 bg-black/60 pointer-events-none" aria-hidden="true" />

    <div ref="playerRef" class="relative z-10 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" :class="'h-full'">
      <div class="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        <video
          ref="videoRef"
          class="h-full w-full object-contain cursor-pointer"
          :src="videoUrl"
          playsinline
          loop
          preload="auto"
          x-webkit-airplay="allow"
          webkit-playsinline="true"
          x5-video-player-type="h5"
          x5-video-player-fullscreen="true"
          x5-video-orientation="portrait"
          @loadeddata="onLoaded"
          @waiting="onWaiting"
          @canplay="onCanPlay"
          @playing="onPlaying"
          @pause="onPause"
          @play="onPlay"
          @error="onError"
          @timeupdate="onTimeUpdate"
          @loadedmetadata="onLoadedMetadata"
          @durationchange="onDurationChange"
          @volumechange="onVolumeChange"
          @click.stop="togglePlay"
        />

        <VideoFeedback
          :requires-login="!!item.requiresLogin"
          :is-premium="isPremium"
          :is-error="isError"
          :is-buffering="isBuffering"
          :is-paused="isPaused"
          :is-login="isLogin"
        />

        <VideoActions
          :class="{ invisible: shouldHideActions }"
          :episode-id="item.episodeId"
          :script-id="item.scriptId"
          :liked="item.liked"
          :like-count="item.likeCount"
          :collected="item.collected"
          :collect-count="item.collectCount"
          :commented="item.commented"
          :comment-count="item.commentCount"
          :share-count="item.shareCount"
          @like="emit('like')"
          @collect="emit('collect')"
          @share="emit('share')"
          @comment="emit('comment')"
        />
      </div>

      <slot name="footer" :video-info="item">
        <FooterForMobile
          v-if="appStore.isMobile"
          :progress="displayProgress"
          :data="item"
          :isFullscreen="isNativeFullscreen"
          :preset="preset || 'recommend'"
          @request-fullscreen="onFullscreenChange"
          @toggle-fullscreen="toggleFullscreen"
          @toggle-info-popup="showEpisodesPanel = $event"
          @episodes-tap="handleEpisodesTap"
          @primary-action="handlePrimaryAction"
        />
        <FooterBarForDesktop
          v-else
          class="shrink-0"
          :is-paused="isPaused"
          :can-control="canControl"
          :display-progress="displayProgress"
          :current-time="currentTime"
          :duration="duration"
          :is-muted="isMuted"
          :is-native-fullscreen="isNativeFullscreen"
          :episode-index="item.episodeIndex"
          :item="item"
          :preset="preset || 'recommend'"
          @toggle-play="togglePlay"
          @toggle-mute="toggleMute"
          @toggle-fullscreen="toggleFullscreen"
          @episodes-tap="handleEpisodesTap"
          @progress-click="handlePcProgressClick"
          @progress-mouse-down="handlePcProgressStart"
          @progress-touch-start="handleProgressTouchStart"
          @progress-touch-move="handleProgressTouchMove"
          @progress-touch-end="handleProgressTouchEnd"
          @primary-action="handlePrimaryAction"
        />
      </slot>
    </div>

    <Comments
      v-if="enableComments"
      v-model:show="showCommentPanel"
      :episode-id="item.episodeId"
      :script-id="item.scriptId"
    />

    <Episodes
      v-if="enableEpisodes"
      v-model:show="showEpisodesPanel"
      :script-id="item.scriptId"
      :episodes="items"
      :active-episode-id="item.episodeId"
      @select-episode="emit('select-episode', $event)"
    />
  </div>
</template>
